import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { EarningApp } from "./apps";

function publicClient() {
  return createClient(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

function validateSpec(s: Partial<EarningApp>): asserts s is EarningApp {
  const req = ["slug", "name", "category", "verdict", "founded", "hq", "regions",
    "minPayout", "payoutMethods", "earningsPerHour", "mechanism", "realSignals",
    "redFlags", "bestFor", "worstFor", "ownerCompany", "yearsOperational",
    "paymentProofNote", "competitorSlugs", "reviewerQuote", "faqExtra"] as const;
  for (const k of req) {
    if ((s as Record<string, unknown>)[k] === undefined) {
      throw new Error(`spec missing required field: ${k}`);
    }
  }
  if (!["Real", "Mixed", "Risky", "Likely Fake"].includes(s.verdict as string)) {
    throw new Error("verdict must be Real / Mixed / Risky / Likely Fake");
  }
  if (!/^[a-z0-9-]+$/.test(s.slug as string)) {
    throw new Error("slug must be kebab-case lowercase letters/numbers/hyphens");
  }
}

/** Auto-append `-real-or-fake` to admin-added slugs for SEO. */
function normalizeSlug(slug: string): string {
  const s = slug.toLowerCase().trim();
  return s.endsWith("-real-or-fake") ? s : `${s}-real-or-fake`;
}

/** Public: list every published custom earning app (server-side, for loaders). */
export const listCustomEarningApps = createServerFn({ method: "GET" }).handler(
  async (): Promise<{ items: EarningApp[] }> => {
    const sb = publicClient();
    const { data, error } = await sb
      .from("earning_apps_custom")
      .select("spec")
      .eq("is_published", true);
    if (error || !data) return { items: [] };
    return { items: data.map((r) => r.spec as EarningApp) };
  },
);

/** Public: fetch one custom app by slug. */
export const getCustomEarningApp = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data }): Promise<{ app: EarningApp | null }> => {
    const sb = publicClient();
    const { data: row, error } = await sb
      .from("earning_apps_custom")
      .select("spec")
      .eq("slug", data.slug)
      .eq("is_published", true)
      .maybeSingle();
    if (error || !row) return { app: null };
    return { app: row.spec as EarningApp };
  });

/** Admin: list all (published + draft). */
export const adminListCustomEarningApps = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId, _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");
    const { data, error } = await context.supabase
      .from("earning_apps_custom")
      .select("slug, spec, is_published, updated_at")
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    return {
      items: (data ?? []).map((r) => ({
        slug: r.slug,
        name: (r.spec as EarningApp)?.name ?? r.slug,
        verdict: (r.spec as EarningApp)?.verdict ?? "Real",
        is_published: r.is_published,
        updated_at: r.updated_at,
      })),
    };
  });

export const adminUpsertCustomEarningApp = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { spec: Partial<EarningApp>; is_published?: boolean }) => d)
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId, _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");
    validateSpec(data.spec);
    const spec = data.spec as EarningApp;
    spec.slug = normalizeSlug(spec.slug);
    const published = data.is_published ?? true;

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("earning_apps_custom").upsert(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { slug: spec.slug, spec: spec as any, is_published: published, created_by: context.userId },
      { onConflict: "slug" },
    );
    if (error) throw new Error(error.message);

    if (published) {
      const url = `https://drhint.com/earning-apps-review/${spec.slug}`;
      let indexnowOk = false;
      try {
        const { broadcastIndexNow } = await import("@/lib/indexnow");
        const res = await broadcastIndexNow([url], { force: true });
        indexnowOk = res.some((r) => r.ok);
      } catch (err) {
        console.error("[earning-apps] indexnow failed:", (err as Error).message);
      }
      try {
        await supabaseAdmin.from("indexing_queue").upsert(
          { url, source: "admin-earning-app", priority: 15, bing_submitted: indexnowOk, bing_last_submitted: new Date().toISOString() },
          { onConflict: "url" },
        );
      } catch (err) {
        console.error("[earning-apps] indexing_queue failed:", (err as Error).message);
      }
      return { ok: true, url: `/earning-apps-review/${spec.slug}`, indexnow: indexnowOk };
    }
    return { ok: true, url: `/earning-apps-review/${spec.slug}`, indexnow: false };
  });

/** Admin: re-submit every published custom earning app to IndexNow (force, no dedupe). */
export const adminResubmitEarningAppsIndexNow = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId, _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");

    const { data, error } = await context.supabase
      .from("earning_apps_custom")
      .select("slug")
      .eq("is_published", true);
    if (error) throw new Error(error.message);

    const urls = [
      "https://drhint.com/earning-apps-review",
      ...(data ?? []).map((r) => `https://drhint.com/earning-apps-review/${r.slug}`),
    ];

    const { broadcastIndexNow } = await import("@/lib/indexnow");
    const results = await broadcastIndexNow(urls, { force: true });
    const ok = results.some((r) => r.ok);

    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const now = new Date().toISOString();
      await supabaseAdmin.from("indexing_queue").upsert(
        urls.map((url) => ({
          url, source: "admin-earning-app", priority: 15,
          bing_submitted: ok, bing_last_submitted: now,
        })),
        { onConflict: "url" },
      );
    } catch (err) {
      console.error("[earning-apps] bulk queue failed:", (err as Error).message);
    }

    return { ok, submitted: urls.length, results };
  });

export const adminDeleteCustomEarningApp = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId, _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("earning_apps_custom").delete().eq("slug", data.slug);
    if (error) throw new Error(error.message);
    return { ok: true };
  });